import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useMediaQuery } from '@mantine/hooks'
import { Box, Group } from '@mantine/core'
import { Profile } from '../../app/models/profile'
import { useStore } from '../../app/stores/store'
import { toast } from 'react-toastify'
import { AmmoScoreBadge, AppButton, AppIconButton, AppMenu, AppModal, AppText, PROFILE_ACCENT_BUTTON_SX, PROFILE_ACTION_BUTTON_SX, ProfileStatStrip, ProfileUserAvatar } from '../../app/common/ui'
import { FiMoreHorizontal } from 'react-icons/fi'
import FollowersModal from './FollowersModal'

const COVER_HEIGHT = 240
const COVER_HEIGHT_MOBILE = 168

interface Props {
  profile: Profile;
  canEdit?: boolean;
}

export default observer(function ProfileHeader ({ profile, canEdit }: Props) {
  const narrow = useMediaQuery('(max-width: 768px)') ?? false
  const { profileStore, userStore } = useStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const history = useHistory()
  const [followersOpened, setFollowersOpened] = useState(false)
  const [followingOpened, setFollowingOpened] = useState(false)
  const [unfollowConfirm, setUnfollowConfirm] = useState(false)

  const isCurrentUser = userStore.user?.username === profile.username
  const canFollow = !isCurrentUser && userStore.isLoggedIn
  const showAnonFollow = !isCurrentUser && !userStore.isLoggedIn
  const username = profile.username

  const handleCoverFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    profileStore.uploadCover(username, file).then(() => {
      toast.success('Cover image updated')
    }).catch((err: any) => {
      toast.error(err?.response?.data ?? 'Failed to upload cover')
    })
    e.target.value = ''
  }

  const doFollow = () => {
    profileStore.follow(username).then(() => toast.success(`Following @${username}`)).catch((err: any) => toast.error(err?.response?.data || 'Failed to update follow status'))
  }

  const doUnfollow = () => {
    profileStore.unfollow(username).then(() => {
      toast.success(`Unfollowed @${username}`)
      setUnfollowConfirm(false)
    }).catch((err: any) => toast.error(err?.response?.data || 'Failed to update follow status'))
  }

  const followersCount = profile.followersCount ?? 0
  const followingCount = profile.followingCount ?? 0

  const statItems = [
    { label: 'AmmoScore', value: profile.ammoScore ?? 0, accent: true },
    { label: 'Followers', value: followersCount, onClick: () => setFollowersOpened(true) },
    { label: 'Following', value: followingCount, onClick: () => setFollowingOpened(true) },
    { label: 'Posts', value: profile.postCount ?? 0 },
    { label: 'Builds', value: profile.buildCount ?? 0 },
    { label: 'Wishlist', value: profile.wishlistCount ?? 0 },
    { label: 'Parts', value: profile.approvedSubmissionsCount ?? 0 }
  ]

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          height: COVER_HEIGHT,
          '@media (max-width: 768px)': { height: COVER_HEIGHT_MOBILE },
          background: profile.coverImageUrl
            ? `url(${profile.coverImageUrl}) center/cover no-repeat`
            : 'linear-gradient(135deg, #0D1117 0%, #1a0e05 40%, #2a1505 60%, #0D1117 100%)',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'repeating-linear-gradient(45deg, rgba(232,93,4,0.05) 0, transparent 1px, transparent 12px)',
            pointerEvents: 'none'
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleCoverFileChange}
        />
        {canEdit && (
          <Box
            component="button"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              position: 'absolute',
              top: 'var(--gs-md)',
              right: 'var(--gs-md)',
              zIndex: 2,
              background: 'rgba(13,17,23,0.75)',
              border: '1px solid var(--gs-border-bright)',
              color: 'var(--gs-text)',
              padding: '5px 10px',
              borderRadius: 5,
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif'
            }}
          >
            Edit Cover
          </Box>
        )}
      </Box>

      <Box
        sx={{
          boxSizing: 'border-box',
          paddingLeft: 'var(--gs-md)',
          paddingRight: 'var(--gs-md)',
          borderBottom: '1px solid var(--gs-border)',
          position: 'relative',
          zIndex: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: -52,
            marginBottom: 12
          }}
        >
          <ProfileUserAvatar
            variant="header"
            size={84}
            imageUrl={profile.image}
            displayName={profile.displayName}
            username={profile.username}
            tier={profile.ammoTier}
          />

          <Box sx={{ display: 'flex', gap: 'var(--gs-sm)', alignItems: 'flex-end', paddingBottom: 'var(--gs-xs)' }}>
            {canEdit && (
              <>
                <AppButton
                  variant="default"
                  component={Link}
                  to={`/profiles/${username}/edit`}
                  sx={PROFILE_ACTION_BUTTON_SX}
                >
                  Edit Profile
                </AppButton>
                <AppMenu shadow="md" width={160} position="bottom-end">
                  <AppMenu.Target>
                    <AppIconButton
                      variant="default"
                      aria-label="Profile actions"
                      sx={{ ...PROFILE_ACTION_BUTTON_SX, width: 36, height: 36 }}
                    >
                      <FiMoreHorizontal size={18} />
                    </AppIconButton>
                  </AppMenu.Target>
                  <AppMenu.Dropdown>
                    <AppMenu.Item>Report</AppMenu.Item>
                    <AppMenu.Item>Share</AppMenu.Item>
                  </AppMenu.Dropdown>
                </AppMenu>
              </>
            )}
            {!canEdit && canFollow && (
              <Box
                component="button"
                type="button"
                onClick={() => (profile.isFollowing ? setUnfollowConfirm(true) : doFollow())}
                disabled={profileStore.updatingFollow}
                sx={{
                  ...PROFILE_ACTION_BUTTON_SX,
                  ...(profile.isFollowing
                    ? { '&:hover': { borderColor: 'var(--gs-accent)' } }
                    : PROFILE_ACCENT_BUTTON_SX)
                }}
              >
                {profileStore.updatingFollow ? '…' : profile.isFollowing ? 'Unfollow' : 'Follow'}
              </Box>
            )}
            {showAnonFollow && (
              <Box
                component="button"
                type="button"
                onClick={() => history.push('/register')}
                sx={PROFILE_ACCENT_BUTTON_SX}
              >
                Follow
              </Box>
            )}
          </Box>
        </Box>

        <AppText
          sx={{
            fontFamily: 'Oswald, sans-serif',
            fontWeight: 700,
            fontSize: 22,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            color: 'var(--gs-text)'
          }}
        >
          {profile.displayName || username}
        </AppText>
        <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)', marginTop: 3, marginBottom: 'var(--gs-sm)' }}>
          @{username}
        </AppText>
        <Box sx={{ marginBottom: 'var(--gs-sm)' }}>
          <AmmoScoreBadge tier={profile.ammoTier ?? 'Newcomer'} score={profile.ammoScore ?? 0} size={narrow ? 'md' : 'lg'} />
        </Box>
        {profile.bio && (
          <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)', lineHeight: 1.6, maxWidth: 560, marginBottom: 'var(--gs-sm)' }}>
            {profile.bio}
          </AppText>
        )}
        <Box sx={{ display: 'flex', gap: 'var(--gs-md)', flexWrap: 'wrap', fontSize: 12 }}>
          <AppText
            component="span"
            sx={{ color: 'var(--gs-muted)', cursor: 'pointer' }}
            onClick={() => setFollowersOpened(true)}
          >
            <AppText component="span" weight={600} sx={{ color: 'var(--gs-text)' }}>{followersCount}</AppText> followers
          </AppText>
          <AppText
            component="span"
            sx={{ color: 'var(--gs-muted)', cursor: 'pointer' }}
            onClick={() => setFollowingOpened(true)}
          >
            <AppText component="span" weight={600} sx={{ color: 'var(--gs-text)' }}>{followingCount}</AppText> following
          </AppText>
        </Box>
      </Box>

      <ProfileStatStrip items={statItems} />

      <FollowersModal username={username} opened={followersOpened} onClose={() => setFollowersOpened(false)} mode="followers" />
      <FollowersModal username={username} opened={followingOpened} onClose={() => setFollowingOpened(false)} mode="following" />
      <AppModal opened={unfollowConfirm} onClose={() => setUnfollowConfirm(false)} title="Unfollow?">
        <AppText size="sm" mb="md">
          Unfollow @{username}? You can follow them again anytime.
        </AppText>
        <Group spacing="xs">
          <AppButton color="red" variant="light" onClick={doUnfollow} loading={profileStore.updatingFollow}>
            Unfollow
          </AppButton>
          <AppButton variant="subtle" onClick={() => setUnfollowConfirm(false)} disabled={profileStore.updatingFollow}>
            Cancel
          </AppButton>
        </Group>
      </AppModal>
    </Box>
  )
})
